/* eslint-disable */

import React, { useState, useEffect, useCallback } from 'react';
import config from '../config';
import { getAuthToken } from '../utils/authStorage';

const API_BASE_URL = `${config.API_BASE_URL}/api/v1`;

const PLATFORM_LABELS = {
  shopify: 'Shopify',
  ebay: 'eBay',
  poshmark: 'Poshmark',
  vinted: 'Vinted',
};

const KpiDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [kpiData, setKpiData] = useState({
    totalSmartscanCompanies: 0,
    totalSmartscanProducts: 0,
    totalSmartlistingCompanies: 0,
    totalSmartlistingProducts: 0,
    totalActions: 0,
    totalErrors: 0,
    totalApiCalls: 0,
    totalAiCalls: 0,
    totalJobs: 0,
    avgPerformance: 0,
    successRate: 0,
    recentActions: [],
    errorLogs: [],
    apiLogs: [],
    performanceLogs: [],
    aiLogs: [],
    jobLogs: [],
    systemLogs: [],
  });
  const [companyPerformance, setCompanyPerformance] = useState({
    overview: null,
    companies: [],
  });

  const msToMinutes = (ms, { decimals = 2 } = {}) => {
    const value = Number(ms);
    if (!Number.isFinite(value)) return '-';
    return (value / 60000).toFixed(decimals);
  };

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    ...(getAuthToken() && { Authorization: `Bearer ${getAuthToken()}` }),
  });

  const buildDateQuery = () => {
    const params = new URLSearchParams();
    if (dateRange.from && dateRange.to) {
      params.set('start_date', dateRange.from);
      params.set('end_date', dateRange.to);
    }
    return params;
  };

  const fetchJson = async (endpoint, { limit = 100 } = {}) => {
    const params = buildDateQuery();
    if (limit) params.set('limit', String(limit));

    const query = params.toString();
    const url = query ? `${API_BASE_URL}${endpoint}?${query}` : `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, { headers: getHeaders() });
    if (!response.ok) {
      throw new Error(`Failed to fetch ${endpoint}: ${response.status}`);
    }
    return response.json();
  };

  const toggleCompanyExpanded = (companyId) => {
    setExpandedCompanies((prev) => ({
      ...prev,
      [companyId]: !prev[companyId],
    }));
  };

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        actionsData,
        errorsData,
        apiData,
        perfData,
        aiData,
        jobData,
        systemData,
        usageSummaryData,
        companyPerformanceData,
      ] = await Promise.all([
        fetchJson('/action'),
        fetchJson('/error'),
        fetchJson('/api'),
        fetchJson('/performance'),
        fetchJson('/ai'),
        fetchJson('/job'),
        fetchJson('/system'),
        fetchJson('/stats/usage-summary', { limit: null }),
        fetchJson('/stats/admin-company-performance', { limit: null }),
      ]);

      const actions = actionsData.success ? actionsData.data : [];
      const errors = errorsData.success ? errorsData.data : [];
      const apiCalls = apiData.success ? apiData.data : [];
      const perfLogs = perfData.success ? perfData.data : [];
      const aiLogs = aiData.success ? aiData.data : [];
      const jobLogs = jobData.success ? jobData.data : [];
      const systemLogs = systemData.success ? systemData.data : [];
      const usageSummary = usageSummaryData.success ? usageSummaryData.data : {};

      const completedActions = actions.filter(
        (action) => action.status === 'SUCCESS' || action.status === 'FAILED'
      );
      const successfulActions = actions.filter((action) => action.status === 'SUCCESS').length;
      const successRate =
        completedActions.length > 0
          ? ((successfulActions / completedActions.length) * 100).toFixed(1)
          : 0;

      const avgPerformance =
        perfLogs.length > 0
          ? msToMinutes(
              perfLogs.reduce((sum, entry) => sum + (entry.duration_ms || 0), 0) / perfLogs.length
            )
          : 0;

      setKpiData({
        totalSmartscanCompanies: usageSummary.smartscan?.total_companies || 0,
        totalSmartscanProducts: usageSummary.smartscan?.total_products || 0,
        totalSmartlistingCompanies: usageSummary.smartlisting?.total_companies || 0,
        totalSmartlistingProducts: usageSummary.smartlisting?.total_products || 0,
        totalActions: actions.length,
        totalErrors: errors.length,
        totalApiCalls: apiCalls.length,
        totalAiCalls: aiLogs.length,
        totalJobs: jobLogs.length,
        avgPerformance,
        successRate,
        recentActions: actions.slice(0, 100),
        errorLogs: errors.slice(0, 100),
        apiLogs: apiCalls.slice(0, 100),
        performanceLogs: perfLogs.slice(0, 100),
        aiLogs: aiLogs.slice(0, 100),
        jobLogs: jobLogs.slice(0, 100),
        systemLogs: systemLogs.slice(0, 100),
      });

      if (companyPerformanceData.success) {
        setCompanyPerformance({
          overview: companyPerformanceData.overview || null,
          companies: companyPerformanceData.companies || [],
        });
      } else {
        setCompanyPerformance({ overview: null, companies: [] });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
    setLoading(false);
  }, [dateRange.from, dateRange.to]);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const renderGlobalDateFilter = () => (
    <div
      style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '30px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Date Filter</h3>
      <div
        style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'end',
        }}
      >
        <div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>From</div>
          <input
            type="date"
            value={dateRange.from}
            onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
            style={{
              padding: '8px 10px',
              border: '1px solid #ddd',
              borderRadius: '6px',
            }}
          />
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>To</div>
          <input
            type="date"
            value={dateRange.to}
            onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
            style={{
              padding: '8px 10px',
              border: '1px solid #ddd',
              borderRadius: '6px',
            }}
          />
        </div>
        <button
          onClick={() => setDateRange({ from: '', to: '' })}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            background: 'white',
            cursor: 'pointer',
          }}
        >
          Clear
        </button>
        <button
          onClick={fetchDashboardData}
          disabled={loading}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '6px',
            background: '#2196f3',
            color: 'white',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? 'Loading...' : 'Apply Filter'}
        </button>
      </div>
      <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#666' }}>
        {dateRange.from && dateRange.to
          ? `Showing data from ${dateRange.from} to ${dateRange.to}`
          : 'Showing all-time data. Select a date range to filter the entire dashboard.'}
      </p>
    </div>
  );

  const renderPlatformCounts = (platforms = {}) =>
    Object.entries(PLATFORM_LABELS).map(([key, label]) => (
      <span
        key={key}
        style={{
          display: 'inline-block',
          marginRight: '8px',
          marginBottom: '4px',
          padding: '2px 8px',
          borderRadius: '4px',
          background: '#f0f0f0',
          fontSize: '12px',
        }}
      >
        {label}: {platforms[key] || 0}
      </span>
    ));

  const renderCompanyPerformance = () => {
    const { overview, companies } = companyPerformance;
    const sortedCompanies = [...companies].sort(
      (a, b) => (b.total_products || 0) - (a.total_products || 0)
    );

    return (
      <div
        style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Company Performance</h3>

        {overview && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '16px',
              marginBottom: '24px',
            }}
          >
            <div style={{ background: '#e3f2fd', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>Companies</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1976d2' }}>
                {overview.total_companies}
              </div>
            </div>
            <div style={{ background: '#f3e5f5', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>Users</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#7b1fa2' }}>
                {overview.total_users}
              </div>
            </div>
            <div style={{ background: '#e8f5e9', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>Products Uploaded</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#388e3c' }}>
                {overview.total_products}
              </div>
            </div>
            <div style={{ background: '#fff3e0', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                Platform Listings
              </div>
              <div>{renderPlatformCounts(overview.platforms)}</div>
            </div>
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left', width: '40px' }}></th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Company</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Users</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Sellers</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Products</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Platforms</th>
              </tr>
            </thead>
            <tbody>
              {sortedCompanies.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No company data found for the selected date range.
                  </td>
                </tr>
              ) : (
                sortedCompanies.map((company) => {
                  const isExpanded = expandedCompanies[company.company_id];
                  const sortedUsers = [...(company.users || [])].sort(
                    (a, b) => (b.products || 0) - (a.products || 0)
                  );

                  return (
                    <React.Fragment key={company.company_id}>
                      <tr style={{ borderBottom: '1px solid #eee', background: isExpanded ? '#fafafa' : 'white' }}>
                        <td style={{ padding: '12px' }}>
                          <button
                            onClick={() => toggleCompanyExpanded(company.company_id)}
                            style={{
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              background: 'white',
                              cursor: 'pointer',
                              width: '28px',
                              height: '28px',
                            }}
                            aria-label={isExpanded ? 'Collapse company' : 'Expand company'}
                          >
                            {isExpanded ? '−' : '+'}
                          </button>
                        </td>
                        <td style={{ padding: '12px', fontWeight: 600 }}>{company.company_name}</td>
                        <td style={{ padding: '12px' }}>{company.total_users}</td>
                        <td style={{ padding: '12px' }}>{company.total_sellers}</td>
                        <td style={{ padding: '12px', fontWeight: 600 }}>{company.total_products}</td>
                        <td style={{ padding: '12px' }}>{renderPlatformCounts(company.platforms)}</td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan="6" style={{ padding: '0 12px 16px 48px', background: '#fafafa' }}>
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Seller / User</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Role</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Products Uploaded</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Platforms</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sortedUsers.length === 0 ? (
                                    <tr>
                                      <td
                                        colSpan="4"
                                        style={{ padding: '20px', textAlign: 'center', color: '#999' }}
                                      >
                                        No users found for this company.
                                      </td>
                                    </tr>
                                  ) : (
                                    sortedUsers.map((user) => (
                                      <tr key={user.user_id} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '10px' }}>{user.user_name}</td>
                                        <td style={{ padding: '10px' }}>
                                          <span
                                            style={{
                                              padding: '3px 8px',
                                              borderRadius: '4px',
                                              background:
                                                user.role === 'seller'
                                                  ? '#e8f5e9'
                                                  : user.role === 'editor'
                                                    ? '#e3f2fd'
                                                    : '#f5f5f5',
                                              fontSize: '12px',
                                              textTransform: 'capitalize',
                                            }}
                                          >
                                            {user.role}
                                          </span>
                                        </td>
                                        <td style={{ padding: '10px', fontWeight: 600 }}>
                                          {user.products || 0}
                                        </td>
                                        <td style={{ padding: '10px' }}>
                                          {renderPlatformCounts(user.platforms)}
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: 0, color: '#333' }}>KPI Dashboard</h1>
        <p style={{ color: '#666' }}>Real-time monitoring from database</p>
      </div>

      {renderGlobalDateFilter()}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #3f51b5',
          }}
        >
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            Total SmartScan Companies
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3f51b5' }}>
            {kpiData.totalSmartscanCompanies}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
            Unique SmartScan companies
          </div>
        </div>

        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #009688',
          }}
        >
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            Total Products Uploading
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#009688' }}>
            {kpiData.totalSmartscanProducts}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
            SmartScan products total
          </div>
        </div>

        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #8bc34a',
          }}
        >
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            Total SmartListing Companies
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#8bc34a' }}>
            {kpiData.totalSmartlistingCompanies}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
            Unique SmartListing companies
          </div>
        </div>

        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #795548',
          }}
        >
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            Total SmartListing Products
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#795548' }}>
            {kpiData.totalSmartlistingProducts}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
            SmartListing products total
          </div>
        </div>

        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #2196f3',
          }}
        >
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Total Actions</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2196f3' }}>
            {kpiData.totalActions}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
            {dateRange.from && dateRange.to ? 'Filtered actions' : 'All time actions'}
          </div>
        </div>

        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #f44336',
          }}
        >
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Total Errors</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f44336' }}>
            {kpiData.totalErrors}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>Error logs count</div>
        </div>

        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #9c27b0',
          }}
        >
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>API Calls</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#9c27b0' }}>
            {kpiData.totalApiCalls}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>External API calls</div>
        </div>

        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #e91e63',
          }}
        >
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>AI Calls</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#e91e63' }}>
            {kpiData.totalAiCalls}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>AI service calls</div>
        </div>

        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #ff9800',
          }}
        >
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Avg Performance</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff9800' }}>
            {kpiData.avgPerformance} min
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>Average duration</div>
        </div>

        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #00bcd4',
          }}
        >
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Total Jobs</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#00bcd4' }}>
            {kpiData.totalJobs}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>Background jobs</div>
        </div>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Success Rate</div>
        <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#4caf50' }}>
          {kpiData.successRate}%
        </div>
        <div
          style={{
            width: '100%',
            height: '10px',
            background: '#e0e0e0',
            borderRadius: '5px',
            marginTop: '10px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${kpiData.successRate}%`,
              height: '100%',
              background: '#4caf50',
              borderRadius: '5px',
            }}
          ></div>
        </div>
      </div>

      {renderCompanyPerformance()}

      <div
        style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Recent Actions</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Action Type</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Module</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Progress</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Start Time</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Duration (min)</th>
              </tr>
            </thead>
            <tbody>
              {kpiData.recentActions.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No actions found for the selected date range.
                  </td>
                </tr>
              ) : (
                kpiData.recentActions.map((action) => (
                  <tr key={action.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{action.id}</td>
                    <td style={{ padding: '12px' }}>{action.action_type}</td>
                    <td style={{ padding: '12px' }}>{action.module || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background:
                            action.status === 'SUCCESS'
                              ? '#4caf50'
                              : action.status === 'FAILED'
                                ? '#f44336'
                                : action.status === 'IN_PROGRESS'
                                  ? '#ff9800'
                                  : '#9e9e9e',
                          color: 'white',
                          fontSize: '12px',
                        }}
                      >
                        {action.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {action.progress !== undefined && action.progress !== null
                        ? `${action.progress}%`
                        : '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {new Date(action.start_time).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {action.duration_ms !== undefined && action.duration_ms !== null
                        ? msToMinutes(action.duration_ms)
                        : 'In Progress'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Recent Errors</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Action ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Error Type</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Error Message</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Severity</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Step</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {kpiData.errorLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No errors found for the selected date range.
                  </td>
                </tr>
              ) : (
                kpiData.errorLogs.map((error) => (
                  <tr key={error.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{error.id}</td>
                    <td style={{ padding: '12px' }}>{error.action_id || '-'}</td>
                    <td style={{ padding: '12px' }}>{error.error_type || '-'}</td>
                    <td
                      style={{
                        padding: '12px',
                        maxWidth: '300px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {error.error_message?.substring(0, 100)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background:
                            error.severity === 'HIGH'
                              ? '#f44336'
                              : error.severity === 'MEDIUM'
                                ? '#ff9800'
                                : '#4caf50',
                          color: 'white',
                          fontSize: '12px',
                        }}
                      >
                        {error.severity || 'MEDIUM'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{error.step || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      {error.created_at ? new Date(error.created_at).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>API Calls Log</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Service Name</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Endpoint</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Method</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Status Code</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Duration (min)</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {kpiData.apiLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No API calls found for the selected date range.
                  </td>
                </tr>
              ) : (
                kpiData.apiLogs.map((api) => (
                  <tr key={api.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{api.id}</td>
                    <td style={{ padding: '12px' }}>{api.service_name || '-'}</td>
                    <td
                      style={{
                        padding: '12px',
                        maxWidth: '250px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {api.endpoint || '-'}
                    </td>
                    <td style={{ padding: '12px' }}>{api.method || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background:
                            api.status_code >= 200 && api.status_code < 300
                              ? '#4caf50'
                              : api.status_code >= 400
                                ? '#f44336'
                                : '#ff9800',
                          color: 'white',
                          fontSize: '12px',
                        }}
                      >
                        {api.status_code || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {api.duration_ms !== undefined && api.duration_ms !== null
                        ? msToMinutes(api.duration_ms)
                        : '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {api.created_at ? new Date(api.created_at).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Performance Metrics</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Action ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Service Name</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Operation</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Duration (min)</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {kpiData.performanceLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No performance logs found for the selected date range.
                  </td>
                </tr>
              ) : (
                kpiData.performanceLogs.map((perf) => (
                  <tr key={perf.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{perf.id}</td>
                    <td style={{ padding: '12px' }}>{perf.action_id || '-'}</td>
                    <td style={{ padding: '12px' }}>{perf.service_name || '-'}</td>
                    <td style={{ padding: '12px' }}>{perf.operation || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      {perf.duration_ms !== undefined && perf.duration_ms !== null
                        ? msToMinutes(perf.duration_ms)
                        : '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background: perf.status === 'SUCCESS' ? '#4caf50' : '#f44336',
                          color: 'white',
                          fontSize: '12px',
                        }}
                      >
                        {perf.status || 'UNKNOWN'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {perf.created_at ? new Date(perf.created_at).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>AI Service Logs</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>AI Service</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Success</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Confidence Score</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Input Quality</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Model Version</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {kpiData.aiLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No AI logs found for the selected date range.
                  </td>
                </tr>
              ) : (
                kpiData.aiLogs.map((ai) => (
                  <tr key={ai.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{ai.id}</td>
                    <td style={{ padding: '12px' }}>{ai.ai_service || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background: ai.success ? '#4caf50' : '#f44336',
                          color: 'white',
                          fontSize: '12px',
                        }}
                      >
                        {ai.success ? 'SUCCESS' : 'FAILED'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{ai.confidence_score || '-'}</td>
                    <td style={{ padding: '12px' }}>{ai.input_quality || '-'}</td>
                    <td style={{ padding: '12px' }}>{ai.model_version || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      {ai.created_at ? new Date(ai.created_at).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Background Jobs</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Action ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Job Type</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Retry Count</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Next Retry At</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {kpiData.jobLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No jobs found for the selected date range.
                  </td>
                </tr>
              ) : (
                kpiData.jobLogs.map((job) => (
                  <tr key={job.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{job.id}</td>
                    <td style={{ padding: '12px' }}>{job.action_id || '-'}</td>
                    <td style={{ padding: '12px' }}>{job.job_type || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background:
                            job.status === 'SUCCESS'
                              ? '#4caf50'
                              : job.status === 'FAILED'
                                ? '#f44336'
                                : job.status === 'PENDING'
                                  ? '#ff9800'
                                  : '#9e9e9e',
                          color: 'white',
                          fontSize: '12px',
                        }}
                      >
                        {job.status || 'PENDING'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {`${job.retry_count || 0}/${job.max_retries || 3}`}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {job.next_retry_at ? new Date(job.next_retry_at).toLocaleString() : '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {job.created_at ? new Date(job.created_at).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>System Logs</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Level</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Message</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Service Name</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {kpiData.systemLogs.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No system logs found for the selected date range.
                  </td>
                </tr>
              ) : (
                kpiData.systemLogs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{log.id}</td>
                    <td style={{ padding: '12px' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background:
                            log.level === 'ERROR'
                              ? '#f44336'
                              : log.level === 'WARN'
                                ? '#ff9800'
                                : log.level === 'INFO'
                                  ? '#2196f3'
                                  : '#9e9e9e',
                          color: 'white',
                          fontSize: '12px',
                        }}
                      >
                        {log.level || 'INFO'}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: '12px',
                        maxWidth: '400px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {log.message || '-'}
                    </td>
                    <td style={{ padding: '12px' }}>{log.service_name || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ position: 'fixed', bottom: '20px', right: '20px' }}>
        <button
          onClick={fetchDashboardData}
          disabled={loading}
          style={{
            background: '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            padding: '10px 20px',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>
    </div>
  );
};

export default KpiDashboard;
